import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SettingsContentCard from '../../../components/Settings/SettingsContentCard';
import IOSSwitch from '../../../components/Switch/IOSSwitch';
import { getIcon } from '../../../utils/IconHandler';
import { getLocations } from '../../../services/LocationService';
import {
  deleteStockCountConfig,
  getStockCountConfig,
  getStockCountConfigPreview,
  listStockCountConfigs,
  saveStockCountConfig,
} from '../../../services/StockCountService';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const REFERENCE_TYPE_OPTIONS = [
  { value: 'SUPPLY', label: 'Insumos' },
  { value: 'PREPARATION', label: 'Preparaciones' },
  { value: 'PRODUCT', label: 'Productos' },
];

const RECURRENCE_OPTIONS = [
  { value: 'DAILY', label: 'Todos los días' },
  { value: 'WEEKLY', label: 'Días de semana puntuales' },
];

// ISO day-of-week (1=Lunes..7=Domingo) -- coincide con lo que espera el backend
// (StockCountService valida esto igual que el resto del sistema con StaffTaskRecurrenceType).
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' },
];

// Umbral orientativo para marcar una carga diaria como potencialmente alta -- no bloquea
// nada, solo ayuda a que quien configura note si el ciclo elegido es poco realista.
const HIGH_LOAD_THRESHOLD = 25;

const MODE_GLOBAL = 'GLOBAL';
const MODE_PER_BRANCH = 'PER_BRANCH';

// ion-icon no es cuadrado por default, así que sin un tamaño fijo el hover circular
// de IconButton sale ovalado -- mismo fix que ya usa ListsTab.jsx.
const ROUND_ICON_BUTTON_SX = {
  width: 32,
  height: 32,
  minWidth: 32,
  minHeight: 32,
  maxWidth: 32,
  maxHeight: 32,
  padding: 0,
  borderRadius: '50%',
  '&:hover': { borderRadius: '50%' },
};

const emptyForm = {
  referenceTypes: [],
  recurrenceType: 'DAILY',
  weeklyDays: [],
  fullCycleDays: 30,
  isActive: true,
};

const isPreviewable = (form) =>
  form.referenceTypes.length > 0 &&
  Number(form.fullCycleDays) > 0 &&
  (form.recurrenceType === 'DAILY' || form.weeklyDays.length > 0);

const recurrenceLabel = (config) => {
  if (config.recurrenceType !== 'WEEKLY') return 'Todos los días';
  const days = (config.weeklyDays || [])
    .map((value) => WEEKDAY_OPTIONS.find((o) => o.value === value)?.label || value)
    .join(', ');
  return `Semanal (${days || 'sin días'})`;
};

const referenceTypesLabel = (config) =>
  (config.referenceTypes || [])
    .map((value) => REFERENCE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value)
    .join(', ') || '-';

const StockCountConfigPage = () => {
  const [locations, setLocations] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [mode, setMode] = useState(MODE_GLOBAL);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingLocationId, setDeletingLocationId] = useState(null);
  const [errors, setErrors] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmTargetMode, setConfirmTargetMode] = useState(null);
  const [switchingMode, setSwitchingMode] = useState(false);

  // En modo global, siempre se edita la fila sin sucursal (locationId null). En modo por
  // sucursal, se edita lo que esté elegido en la tabla -- o nada, si todavía no se eligió.
  const targetLocationId = mode === MODE_GLOBAL ? null : selectedLocationId;

  const loadConfigs = async () => {
    const result = await listStockCountConfigs();
    setConfigs(result || []);
    return result || [];
  };

  const loadFormFor = async (locationId) => {
    try {
      setFormLoading(true);
      const config = await getStockCountConfig(locationId);
      setForm(config
        ? {
          referenceTypes: config.referenceTypes || [],
          recurrenceType: config.recurrenceType || 'DAILY',
          weeklyDays: config.weeklyDays || [],
          fullCycleDays: config.fullCycleDays || 30,
          isActive: config.isActive !== false,
        }
        : emptyForm);
      setErrors({});
    } catch (error) {
      console.error('Error loading stock count config', error);
      SnackbarUtils.error('No pudimos cargar esa configuración');
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        const [locationData, configData] = await Promise.all([getLocations(), loadConfigs()]);
        // Las plantas se listan para poder configurarles un conteo propio (insumos de producción);
        // el modo global solo aplica a sucursales -- lo resuelve el backend.
        setLocations(locationData || []);
        // Si ya hay configuraciones por sucursal, arrancamos ahí -- son más específicas que
        // una eventual config global remanente (caso de datos previos a este modo exclusivo).
        const hasOverrides = (configData || []).some((c) => c.locationId);
        const initialMode = hasOverrides ? MODE_PER_BRANCH : MODE_GLOBAL;
        setMode(initialMode);
        if (initialMode === MODE_GLOBAL) {
          await loadFormFor(null);
        }
      } catch (error) {
        console.error('Error loading stock count config', error);
        SnackbarUtils.error('No pudimos cargar la configuración de conteo de stock');
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line
  }, []);

  const applyModeChange = async (nextMode) => {
    setMode(nextMode);
    if (nextMode === MODE_GLOBAL) {
      await loadFormFor(null);
    } else {
      setSelectedLocationId(null);
      setForm(emptyForm);
    }
  };

  // Los dos modos son excluyentes: si hay datos del otro lado, hay que confirmar porque
  // cambiar de modo los borra (si no, quedarían conviviendo config global y por sucursal,
  // que es justo lo que se quiere evitar).
  const requestModeChange = (nextMode) => {
    if (nextMode === mode) return;
    const hasGlobal = configs.some((c) => !c.locationId);
    const overrideCount = configs.filter((c) => c.locationId).length;
    const needsConfirm = (nextMode === MODE_GLOBAL && overrideCount > 0) || (nextMode === MODE_PER_BRANCH && hasGlobal);
    if (needsConfirm) {
      setConfirmTargetMode(nextMode);
    } else {
      applyModeChange(nextMode);
    }
  };

  const confirmModeSwitch = async () => {
    const nextMode = confirmTargetMode;
    try {
      setSwitchingMode(true);
      if (nextMode === MODE_GLOBAL) {
        const overrides = configs.filter((c) => c.locationId);
        await Promise.all(overrides.map((c) => deleteStockCountConfig(c.locationId)));
      } else {
        await deleteStockCountConfig(null);
      }
      await loadConfigs();
      await applyModeChange(nextMode);
      SnackbarUtils.success('Modo de conteo actualizado');
    } catch (error) {
      console.error('Error switching stock count mode', error);
      SnackbarUtils.error('No pudimos cambiar el modo de conteo');
    } finally {
      setSwitchingMode(false);
      setConfirmTargetMode(null);
    }
  };

  const handleSelectBranch = (locationId) => {
    setSelectedLocationId(locationId);
    loadFormFor(locationId);
  };

  // Previsualización con debounce: cada vez que cambian los valores que afectan el cálculo,
  // le preguntamos al backend cuántos ítems por sesión tocaría con ESTOS valores (sin guardar
  // nada) -- todas las sucursales en modo global, o solo la elegida en modo por sucursal.
  useEffect(() => {
    const previewTarget = mode === MODE_PER_BRANCH ? selectedLocationId : null;
    const canPreview = (mode === MODE_GLOBAL || selectedLocationId != null) && isPreviewable(form);
    if (loading || formLoading || !canPreview) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const result = await getStockCountConfigPreview({
          referenceTypes: form.referenceTypes,
          recurrenceType: form.recurrenceType,
          weeklyDays: form.recurrenceType === 'WEEKLY' ? form.weeklyDays : [],
          fullCycleDays: form.fullCycleDays,
          locationId: previewTarget,
        });
        setPreview(result);
      } catch (error) {
        console.error('Error loading stock count preview', error);
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [loading, formLoading, mode, selectedLocationId, form.referenceTypes, form.recurrenceType, form.weeklyDays, form.fullCycleDays]);

  const toggleWeekday = (value) => {
    setForm((previous) => {
      const exists = previous.weeklyDays.includes(value);
      const nextDays = exists
        ? previous.weeklyDays.filter((day) => day !== value)
        : [...previous.weeklyDays, value].sort((a, b) => a - b);
      return { ...previous, weeklyDays: nextDays };
    });
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.referenceTypes.length) {
      nextErrors.referenceTypes = 'Elegí al menos un tipo de ítem';
    }
    if (form.recurrenceType === 'WEEKLY' && !form.weeklyDays.length) {
      nextErrors.weeklyDays = 'Elegí al menos un día de la semana';
    }
    if (!form.fullCycleDays || form.fullCycleDays <= 0) {
      nextErrors.fullCycleDays = 'Tiene que ser un número mayor a 0';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (mode === MODE_PER_BRANCH && !selectedLocationId) {
      SnackbarUtils.error('Elegí primero una sucursal de la lista');
      return;
    }
    if (!validate()) {
      SnackbarUtils.error('Completá los campos requeridos');
      return;
    }
    try {
      setSaving(true);
      await saveStockCountConfig({
        location_id: targetLocationId,
        recurrence_type: form.recurrenceType,
        weekly_days: form.recurrenceType === 'WEEKLY' ? form.weeklyDays : [],
        full_cycle_days: form.fullCycleDays,
        reference_types: form.referenceTypes,
        is_active: form.isActive,
      });
      SnackbarUtils.success('Configuración guardada');
      await loadConfigs();
    } catch (error) {
      console.error('Error saving stock count config', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBranchConfig = async (locationId) => {
    try {
      setDeletingLocationId(locationId);
      await deleteStockCountConfig(locationId);
      SnackbarUtils.success('Esa sucursal quedó sin configurar (no se va a contar hasta que la configures de nuevo)');
      await loadConfigs();
      if (selectedLocationId === locationId) {
        setForm(emptyForm);
      }
    } catch (error) {
      console.error('Error deleting stock count config', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos quitar esa configuración');
    } finally {
      setDeletingLocationId(null);
    }
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const configByLocationId = new Map(configs.filter((c) => c.locationId).map((c) => [c.locationId, c]));
  const selectedLocation = locations.find((location) => location.id === selectedLocationId);
  const editingTitle = mode === MODE_GLOBAL
    ? 'Editando: configuración global'
    : selectedLocation
      ? `Editando: ${selectedLocation.name}`
      : 'Elegí una sucursal';

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SettingsContentCard
          title="Modo de conteo"
          description="Una configuración para todas las sucursales, o una configuración propia por cada sucursal -- no las dos a la vez."
        >
          <FormControl disabled={switchingMode}>
            <RadioGroup row value={mode} onChange={(e) => requestModeChange(e.target.value)}>
              <FormControlLabel value={MODE_GLOBAL} control={<Radio />} label="Global (misma configuración para todas las sucursales)" />
              <FormControlLabel value={MODE_PER_BRANCH} control={<Radio />} label="Por sucursal (cada una con la suya)" />
            </RadioGroup>
          </FormControl>
        </SettingsContentCard>

        {mode === MODE_PER_BRANCH && (
          <SettingsContentCard
            title="Sucursales"
            description="Elegí una sucursal para configurarla. Las que no tengan configuración propia no van a generar conteos."
          >
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Qué cuenta</TableCell>
                    <TableCell>Frecuencia</TableCell>
                    <TableCell align="right">Ciclo</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locations.map((location) => {
                    const config = configByLocationId.get(location.id);
                    return (
                      <TableRow key={location.id} hover selected={selectedLocationId === location.id}>
                        <TableCell>
                          {location.name}
                          {location.isPlant && <Chip size="small" label="Planta" color="success" sx={{ ml: 1 }} />}
                        </TableCell>
                        {config ? (
                          <>
                            <TableCell>{referenceTypesLabel(config)}</TableCell>
                            <TableCell>{recurrenceLabel(config)}</TableCell>
                            <TableCell align="right">{config.fullCycleDays} días</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={config.isActive ? 'Activo' : 'Inactivo'} color={config.isActive ? 'success' : 'default'} />
                            </TableCell>
                          </>
                        ) : (
                          <TableCell colSpan={4}><Typography variant="body2" color="text.secondary">Sin configurar</Typography></TableCell>
                        )}
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => handleSelectBranch(location.id)} sx={ROUND_ICON_BUTTON_SX}>
                                {getIcon('edit')}
                              </IconButton>
                            </Tooltip>
                            {config && (
                              <Tooltip title="Quitar configuración (deja de contarse)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteBranchConfig(location.id)}
                                  disabled={deletingLocationId === location.id}
                                  sx={ROUND_ICON_BUTTON_SX}
                                >
                                  {deletingLocationId === location.id ? <CircularProgress size={16} /> : getIcon('trash')}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SettingsContentCard>
        )}

        {(mode === MODE_GLOBAL || selectedLocationId) && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
          <SettingsContentCard
            title={editingTitle}
            description="El sistema arma automáticamente qué contar cada día en cada sucursal, priorizando lo que hace más tiempo no se cuenta."
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(errors.referenceTypes)}>
                  <InputLabel shrink>Qué se cuenta</InputLabel>
                  <Select
                    label="Qué se cuenta"
                    notched
                    displayEmpty
                    multiple
                    disabled={formLoading}
                    value={form.referenceTypes}
                    onChange={(e) => setForm((p) => ({ ...p, referenceTypes: e.target.value }))}
                    renderValue={(selected) => selected.length === 0
                      ? 'Elegí uno o más tipos'
                      : selected.map((value) => REFERENCE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value).join(', ')}
                  >
                    {REFERENCE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={form.referenceTypes.includes(option.value)} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.referenceTypes && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{errors.referenceTypes}</Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={formLoading}>
                  <InputLabel>Frecuencia</InputLabel>
                  <Select
                    label="Frecuencia"
                    value={form.recurrenceType}
                    onChange={(e) => setForm((p) => ({ ...p, recurrenceType: e.target.value, weeklyDays: e.target.value === 'WEEKLY' ? p.weeklyDays : [] }))}
                  >
                    {RECURRENCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {form.recurrenceType === 'WEEKLY' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Días de la semana</Typography>
                  <FormGroup row>
                    {WEEKDAY_OPTIONS.map((weekday) => (
                      <FormControlLabel
                        key={weekday.value}
                        control={
                          <Checkbox
                            checked={form.weeklyDays.includes(weekday.value)}
                            onChange={() => toggleWeekday(weekday.value)}
                          />
                        }
                        label={weekday.label}
                      />
                    ))}
                  </FormGroup>
                  {errors.weeklyDays && (
                    <Typography variant="caption" color="error">{errors.weeklyDays}</Typography>
                  )}
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  label="Días para completar un ciclo"
                  type="number"
                  fullWidth
                  disabled={formLoading}
                  value={form.fullCycleDays}
                  onChange={(e) => setForm((p) => ({ ...p, fullCycleDays: parseInt(e.target.value, 10) || '' }))}
                  error={Boolean(errors.fullCycleDays)}
                  helperText={errors.fullCycleDays || 'En cuántos días tiene que quedar contado el 100% del universo habilitado, por sucursal'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Estado
                </Typography>
                <IOSSwitch
                  checked={form.isActive}
                  disabled={formLoading}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
              </Grid>

              <Grid item xs={12}>
                <Button variant="contained" onClick={handleSave} disabled={saving || formLoading}>
                  {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar cambios'}
                </Button>
              </Grid>
            </Grid>
          </SettingsContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
          <SettingsContentCard
            title="Previsualización de carga"
            description={`Con estos valores (todavía sin guardar), esto es lo que tocaría contar en promedio por sesión en ${mode === MODE_GLOBAL ? 'cada sucursal' : selectedLocation?.name || 'esa sucursal'}.`}
          >
            {formLoading || (previewLoading && !preview) ? (
              <Skeleton variant="rectangular" height={140} />
            ) : !isPreviewable(form) ? (
              <Alert severity="info">Completá qué se cuenta, la frecuencia y los días de ciclo para ver la previsualización.</Alert>
            ) : !preview || !preview.length ? (
              <Alert severity="warning">No encontramos ítems del tipo elegido para contar en ese alcance.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sucursal</TableCell>
                      <TableCell align="right">Universo habilitado</TableCell>
                      <TableCell align="right">Días de conteo en el ciclo</TableCell>
                      <TableCell align="right">Ítems por sesión (estimado)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.map((branch) => (
                      <TableRow key={branch.locationId} hover>
                        <TableCell>{branch.locationName}</TableCell>
                        <TableCell align="right">{branch.universeSize}</TableCell>
                        <TableCell align="right">{branch.countDaysInCycle}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={branch.itemsPerSession}
                            color={branch.itemsPerSession > HIGH_LOAD_THRESHOLD ? 'warning' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SettingsContentCard>
          </Grid>
        </Grid>
        )}
      </Box>

      <Dialog open={Boolean(confirmTargetMode)} onClose={() => setConfirmTargetMode(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cambiar el modo de conteo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmTargetMode === MODE_GLOBAL
              ? `Pasar a modo global va a eliminar las ${configs.filter((c) => c.locationId).length} configuraciones por sucursal que ya existen. A partir de ahora, una sola configuración va a aplicar a todas las sucursales.`
              : 'Pasar a modo por sucursal va a eliminar la configuración global. Vas a tener que configurar cada sucursal individualmente -- la que no configures no se va a contar.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTargetMode(null)} disabled={switchingMode}>Cancelar</Button>
          <LoadingButton onClick={confirmModeSwitch} loading={switchingMode} variant="contained" color="error">
            Confirmar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockCountConfigPage;
